import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Building2, AlertTriangle, Sparkles, ChevronDown, Star, Download } from "lucide-react";
import { decodeRefNum } from "../utils/refNumCrypto";
import { countryFromSearch } from "../utils/caseHref";
import {
  getPropertyAssessmentDetail,
  getPropertyVehicle,
  getPropertyMediaBaseUrl,
} from "../services/propertyApiService";
import {
  toPropertyOverview,
  groupPropertyImagesBySide,
  buildPropertyImageUrl,
  resolveSummaryEvidenceUrls,
  isUnobservedValue,
} from "../services/adapters/propertyAssessment";
import { usePropertyAiSummary } from "../hooks/usePropertyAiSummary";
import { usePropertyDownloadReport } from "../hooks/usePropertyDownloadReport";
import { getStatusInfo, formatDateTime } from "../utils/statusMapping";
import { formatUploadDuration } from "../utils/uploadDuration";
import CustomMap from "../components/CustomMap";
import latviaFlag from "../assets/icons/cases/latvia-flag.svg";
import lithuaniaFlag from "../assets/icons/cases/lithuania-flag.svg";
import estoniaFlag from "../assets/icons/cases/estonia-flag.svg";

const COUNTRY_FLAGS = {
  latvia: latviaFlag,
  lv: latviaFlag,
  lat: latviaFlag,
  lithuania: lithuaniaFlag,
  lt: lithuaniaFlag,
  ltu: lithuaniaFlag,
  estonia: estoniaFlag,
  ee: estoniaFlag,
  est: estoniaFlag,
};

const getCountryFlagSrc = (countryName) =>
  COUNTRY_FLAGS[String(countryName || "").toLowerCase().trim()] || "";

// Read-only Overview only, per the property flow's first pass — Approve,
// Documents and POI have no property endpoint yet, so this intentionally
// stays a much smaller fork of CaseDetails.js rather than carrying over
// vehicle-only chrome (stencil diagram, make/model header, num_plate-as-id)
// that has no property equivalent. AI Summary is GET /get_summary on this
// same property API — see propertyApiService.js.

const IMAGE_SECTIONS = [
  { key: "exterior", label: "Exterior Images" },
  { key: "interior", label: "Interior Images" },
  { key: "additional", label: "Additional Images" },
];

const SEVERITY_COLORS = {
  minor: { bg: "#fef3c7", fg: "#92400e" },
  moderate: { bg: "#fed7aa", fg: "#9a3412" },
  major: { bg: "#fecaca", fg: "#991b1b" },
};

const CONDITION_COLORS = {
  excellent: { bg: "#dcfce7", fg: "#166534" },
  good: { bg: "#dcfce7", fg: "#166534" },
  fair: { bg: "#fef9c3", fg: "#854d0e" },
  poor: { bg: "#fed7aa", fg: "#9a3412" },
  severe: { bg: "#fecaca", fg: "#991b1b" },
};

// "LIVING_HOME_INSPECTION" -> "Living Home Inspection"
const formatEnumLabel = (value) => {
  if (!value) return "";
  return String(value)
    .split("_")
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(" ");
};

function StatTile({ label, value }) {
  return (
    <div className="bg-white rounded-[12px] border border-border-light p-4 shadow-sm text-center">
      <div className="text-[#1f2937] text-2xl font-bold leading-tight">{value}</div>
      <div className="text-[#6b7280] text-xs font-medium mt-1">{label}</div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div>
      <dt className="text-xs text-text-secondary uppercase tracking-wide">{label}</dt>
      <dd className="text-sm text-text-primary mt-1">{value || "—"}</dd>
    </div>
  );
}

// user_rating is 0-5. Out-of-range values still render — clamped, not
// hidden — so a future 0 or a surprising 6 from the API is visible as a
// star count rather than silently dropped.
function StarRating({ rating }) {
  const filled = Math.max(0, Math.min(5, Math.round(rating)));
  return (
    <div className="flex items-center gap-1" role="img" aria-label={`${filled} out of 5 stars`}>
      {Array.from({ length: 5 }, (_, i) => (
        <Star
          key={i}
          className={`w-4 h-4 ${i < filled ? "fill-[#f5b429] text-[#f5b429]" : "text-border-light"}`}
        />
      ))}
    </div>
  );
}

function Chip({ label, colors }) {
  if (!label) return null;
  const c = colors || { bg: "#f3f4f6", fg: "#374151" };
  return (
    <span
      className="inline-flex items-center rounded-[6px] px-2 py-1 text-xs font-semibold"
      style={{ background: c.bg, color: c.fg }}
    >
      {formatEnumLabel(label)}
    </span>
  );
}

function EvidenceThumbs({ indices, images, mediaBaseUrl }) {
  const urls = resolveSummaryEvidenceUrls(indices, images, mediaBaseUrl);
  if (urls.length === 0) return null;
  return (
    <div className="flex gap-1 mt-2">
      {urls.map((url, i) => (
        <a key={i} href={url} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-[4px] overflow-hidden bg-gray-100 block">
          <img alt={`Evidence ${i + 1}`} className="w-full h-full object-cover" src={url} />
        </a>
      ))}
    </div>
  );
}

const STAT_TONES = {
  good: "text-green-700",
  bad: "text-red-700",
  neutral: "text-text-primary",
};

// A stat tile styled after the old TriNetra "Property Image Summary" page —
// label on top, value below — used for the report's header row. `tone`
// colors the value for the two cards where that reading matters at a glance
// (Overall Condition, Risk Flags); every other card stays neutral.
function SummaryStatCard({ label, value, sub, tone = "neutral" }) {
  return (
    <div className="bg-white rounded-[12px] border border-border-light p-4">
      <div className="text-[11px] font-semibold text-text-secondary uppercase tracking-wide">{label}</div>
      <div className={`text-sm font-semibold mt-1.5 ${STAT_TONES[tone] || STAT_TONES.neutral}`}>{value || "—"}</div>
      {sub && <div className="text-xs text-text-secondary mt-0.5">{sub}</div>}
    </div>
  );
}

// The model's own construction/condition narration arrives as one long
// semicolon-joined string (dozens of short observations run together), which
// reads as an unreadable wall of text on one line. Splitting on "; " turns
// it back into the bullet list it actually is, collapsed behind "Show N
// more" the same way the old TriNetra page does — the underlying data is
// identical, this just stops presenting it worse than the source justifies.
function BulletList({ text, limit = 6 }) {
  const [expanded, setExpanded] = useState(false);
  const items = String(text)
    .split(/;\s*/)
    .map((s) => s.trim())
    .filter(Boolean);

  if (items.length <= 1) {
    return <p className="text-sm text-text-primary">{items[0] || text}</p>;
  }

  const shown = expanded ? items : items.slice(0, limit);
  const remaining = items.length - shown.length;

  return (
    <div>
      <ul className="list-disc list-inside text-sm text-text-primary space-y-1">
        {shown.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
      {remaining > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="mt-2 text-xs font-semibold text-primary hover:underline"
        >
          Show {remaining} more
        </button>
      )}
      {expanded && items.length > limit && (
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="mt-2 ml-3 text-xs font-semibold text-text-secondary hover:underline"
        >
          Show less
        </button>
      )}
    </div>
  );
}

// result.construction is an open bag of fields the model fills in as it can
// (wall_material, roof_material, stories, ...) — rendered generically rather
// than one DetailRow per named field, so a field the model starts/stops
// sending doesn't need a code change here. Short values (a story count, an
// era estimate) read fine as a label/value pair; long ones (the semicolon-
// joined narrations) get their own bulleted subsection instead.
function ConstructionPanel({ construction }) {
  const allEntries = Object.entries(construction || {}).filter(
    ([, v]) => v !== null && v !== undefined && v !== "",
  );
  // Fields the model answered "not shown"/"unclear" for aren't facts about
  // the building — they're the model declining to guess. Listing "Wall
  // Material: not shown" as its own labeled row reads as data when it isn't
  // any, so those fields are dropped rather than displayed.
  const entries = allEntries.filter(([, v]) => !isUnobservedValue(v));

  if (allEntries.length === 0) {
    return <p className="text-sm text-text-secondary">No construction details available.</p>;
  }
  if (entries.length === 0) {
    return (
      <p className="text-sm text-text-secondary">
        No construction details were visible in the submitted photos.
      </p>
    );
  }

  const isLong = (v) => typeof v === "string" && (v.includes(";") || v.length > 60);
  const shortEntries = entries.filter(([, v]) => !isLong(v));
  const longEntries = entries.filter(([, v]) => isLong(v));

  return (
    <div className="space-y-4">
      {shortEntries.length > 0 && (
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
          {shortEntries.map(([key, value]) => (
            <DetailRow
              key={key}
              label={formatEnumLabel(key)}
              value={typeof value === "boolean" ? (value ? "Yes" : "No") : formatEnumLabel(String(value)) || String(value)}
            />
          ))}
        </dl>
      )}
      {longEntries.map(([key, value]) => (
        <div key={key}>
          <h4 className="text-sm font-semibold text-text-primary mb-1">{formatEnumLabel(key)}</h4>
          <BulletList text={value} />
        </div>
      ))}
    </div>
  );
}

// The Systems table from the old page: one row per building system, a
// checkmark for whether the photos actually showed it, evidence thumbnails
// when they did. condition/observations render inline when the model
// provides them, so this degrades gracefully to a plain seen/not-seen list
// when it doesn't (as on a small photo set).
function SystemsPanel({ systems, images, mediaBaseUrl }) {
  const entries = Object.entries(systems || {});
  if (entries.length === 0) {
    return <p className="text-sm text-text-secondary">No system details available.</p>;
  }
  // A truthy condition/observations string is not the same as an observed
  // one — the model fills both in with phrasing like "not shown" when the
  // photos didn't cover a system, which used to read as "seen" (any
  // non-empty string) and drew a green checkmark on a system nobody
  // photographed.
  const wasSeen = (sys) =>
    Boolean(sys?.evidence_images?.length) ||
    (Boolean(sys?.condition) && !isUnobservedValue(sys.condition)) ||
    (Boolean(sys?.observations) && !isUnobservedValue(sys.observations));
  const anySeen = entries.some(([, sys]) => wasSeen(sys));

  return (
    <div>
      <div className="divide-y divide-border-light">
        {entries.map(([key, sys]) => {
          const seen = wasSeen(sys);
          const conditionChip = sys?.condition && !isUnobservedValue(sys.condition) ? sys.condition : null;
          // "Not shown in the uploaded photos" already says this system
          // wasn't seen — repeating it as an observations line under an
          // unrelated system name adds nothing.
          const observations =
            sys?.observations && !isUnobservedValue(sys.observations) ? sys.observations : null;
          return (
            <div key={key} className="py-2.5 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-text-primary">{formatEnumLabel(key)}</span>
                  {conditionChip && <Chip label={conditionChip} colors={CONDITION_COLORS[conditionChip]} />}
                </div>
                {observations && <p className="text-xs text-text-secondary mt-0.5">{observations}</p>}
                <EvidenceThumbs indices={sys?.evidence_images} images={images} mediaBaseUrl={mediaBaseUrl} />
              </div>
              <span
                className={`shrink-0 text-base leading-none mt-0.5 ${seen ? "text-green-600" : "text-gray-300"}`}
                aria-label={seen ? "Seen in photos" : "Not seen in photos"}
              >
                {seen ? "✓" : "✗"}
              </span>
            </div>
          );
        })}
      </div>
      {!anySeen && (
        <p className="text-xs text-text-secondary mt-3">
          None of the building systems were visible in the photos.
        </p>
      )}
    </div>
  );
}

const PRIORITY_COLORS = {
  high: { bg: "#fee2e2", fg: "#991b1b" },
  normal: { bg: "#f3f4f6", fg: "#374151" },
  low: { bg: "#f3f4f6", fg: "#6b7280" },
};

const CHECKLIST_STATUS_COLORS = {
  present: CONDITION_COLORS.good,
  absent: SEVERITY_COLORS.major,
  unclear: { bg: "#f3f4f6", fg: "#374151" },
  not_assessed: { bg: "#f3f4f6", fg: "#6b7280" },
};

// The region-specific checklist (fire/electrical/water/etc. items relevant
// to where the property is) — same table the old portal shows under "Region
// Checklist — <region name>".
function RegionChecklistTable({ checklist, regionName, images, mediaBaseUrl }) {
  if (!checklist?.length) return null;
  return (
    <div>
      <h3 className="text-sm font-semibold text-text-primary mb-3">
        Region Checklist{regionName ? ` — ${regionName}` : ""}
      </h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left text-[11px] text-text-secondary uppercase tracking-wide border-b border-border-light">
              <th className="py-2 pr-3 font-semibold">Item</th>
              <th className="py-2 pr-3 font-semibold">Category</th>
              <th className="py-2 pr-3 font-semibold">Priority</th>
              <th className="py-2 pr-3 font-semibold">Status</th>
              <th className="py-2 font-semibold">Evidence</th>
            </tr>
          </thead>
          <tbody>
            {checklist.map((c, i) => (
              <tr key={c.key || i} className="border-b border-border-light last:border-0">
                <td className="py-2.5 pr-3 text-text-primary whitespace-nowrap">{c.label}</td>
                <td className="py-2.5 pr-3 text-text-secondary">{formatEnumLabel(c.category)}</td>
                <td className="py-2.5 pr-3"><Chip label={c.priority} colors={PRIORITY_COLORS[c.priority]} /></td>
                <td className="py-2.5 pr-3"><Chip label={c.status} colors={CHECKLIST_STATUS_COLORS[c.status]} /></td>
                <td className="py-2.5">
                  <EvidenceThumbs indices={c.evidence_images} images={images} mediaBaseUrl={mediaBaseUrl} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// The thumbnail strip the old page calls "Image Inventory" — every photo the
// analysis looked at, one-line captioned with what the model saw in it.
// image_inventory's `image` field is the same 1-based index evidence_images
// uses, resolved against the summary's own images list (not the case's full
// upload list — see resolveSummaryEvidenceUrls).
function ImageInventoryStrip({ inventory, images, mediaBaseUrl }) {
  if (!inventory?.length) return null;
  const byIndex = new Map((images || []).map((img) => [img?.index, img]));
  return (
    <div>
      <h3 className="text-sm font-semibold text-text-primary mb-3">Image Inventory</h3>
      <div className="flex gap-3 overflow-x-auto pb-1">
        {inventory.map((entry) => {
          const url = buildPropertyImageUrl(mediaBaseUrl, byIndex.get(entry.image)?.picture);
          return (
            <a
              key={entry.image}
              href={url || undefined}
              target="_blank"
              rel="noreferrer"
              title={entry.label}
              className="shrink-0 w-24"
            >
              <div className="w-24 h-24 rounded-[8px] overflow-hidden bg-gray-100">
                {url && (
                  <img alt={entry.label || `Image ${entry.image}`} className="w-full h-full object-cover" src={url} />
                )}
              </div>
              <p className="mt-1 text-[11px] text-text-secondary line-clamp-2">{entry.label}</p>
            </a>
          );
        })}
      </div>
    </div>
  );
}

// Renders GET /get_summary's result — see propertyApiService.js for the
// endpoint. images (the summary's own image list) resolves evidence_images
// indices via resolveSummaryEvidenceUrls. Laid out to match the old
// ergobalticproperty portal's "Property Image Summary" page: a stat-card
// header, Construction/Systems side by side, the region checklist table,
// then the image inventory strip.
const CONDITION_TONE = {
  excellent: "good",
  good: "good",
  fair: "neutral",
  poor: "bad",
  severe: "bad",
};

// A single number an underwriter can act on without reading the whole
// report: high-priority region-checklist items that aren't confirmed
// present (missing smoke detectors, unclear electrical, etc.), plus any
// visible damage or named risk factor. The job id and raw timestamp the
// report used to lead with tell a reviewer nothing about the property
// itself — this does.
function countRiskFlags(result) {
  const highPriorityGaps = (result?.checklist || []).filter(
    (c) => c?.priority === "high" && c?.status !== "present",
  ).length;
  const damageCount = result?.visible_damages?.length || 0;
  const riskFactorCount = result?.risk_factors?.length || 0;
  return {
    total: highPriorityGaps + damageCount + riskFactorCount,
    highPriorityGaps,
    damageCount,
    riskFactorCount,
  };
}

function AiSummaryResult({ result, images, completedAt, mediaBaseUrl, onRegenerate }) {
  const riskFlags = countRiskFlags(result);
  const riskSub = riskFlags.total === 0
    ? "No issues raised"
    : [
      riskFlags.highPriorityGaps ? `${riskFlags.highPriorityGaps} high-priority` : null,
      riskFlags.damageCount ? `${riskFlags.damageCount} damage` : null,
      riskFlags.riskFactorCount ? `${riskFlags.riskFactorCount} risk factor${riskFlags.riskFactorCount > 1 ? "s" : ""}` : null,
    ].filter(Boolean).join(" · ");

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span className="inline-flex items-center gap-1.5 rounded-[6px] bg-green-100 text-green-800 text-xs font-semibold px-2 py-1">
          <span className="w-1.5 h-1.5 rounded-full bg-green-600" />
          Completed
        </span>
        <div className="flex items-center gap-3">
          {completedAt && (
            <span className="text-xs text-text-secondary">Generated {formatDateTime(completedAt)}</span>
          )}
          <button
            type="button"
            onClick={onRegenerate}
            className="text-xs font-semibold text-primary hover:underline"
          >
            Regenerate
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <SummaryStatCard label="Property Type" value={formatEnumLabel(result.property_type)} />
        <SummaryStatCard
          label="Overall Condition"
          value={formatEnumLabel(result.overall_condition?.rating)}
          tone={CONDITION_TONE[result.overall_condition?.rating] || "neutral"}
        />
        <SummaryStatCard label="Occupancy" value={formatEnumLabel(result.occupancy?.status || result.occupancy?.occupancy_type)} />
        <SummaryStatCard label="Images Analysed" value={result.image_count ?? images?.length ?? null} />
        <SummaryStatCard
          label="Region Profile"
          value={result.region_profile?.name}
          sub={result.region_profile?.key}
        />
        <SummaryStatCard
          label="Risk Flags"
          value={riskFlags.total === 0 ? "None" : riskFlags.total}
          sub={riskSub}
          tone={riskFlags.total === 0 ? "good" : "bad"}
        />
      </div>

      {result.narrative && (
        <p className="text-sm text-text-primary leading-relaxed">{result.narrative}</p>
      )}

      {result.overall_condition?.explanation && (
        <p className="text-sm text-text-secondary">{result.overall_condition.explanation}</p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-border-light rounded-[8px] p-4">
          <h3 className="text-sm font-semibold text-text-primary mb-3">Construction</h3>
          <ConstructionPanel construction={result.construction} />
        </div>
        <div className="border border-border-light rounded-[8px] p-4">
          <h3 className="text-sm font-semibold text-text-primary mb-3">Systems</h3>
          <SystemsPanel systems={result.systems} images={images} mediaBaseUrl={mediaBaseUrl} />
        </div>
      </div>

      <RegionChecklistTable
        checklist={result.checklist}
        regionName={result.region_profile?.name}
        images={images}
        mediaBaseUrl={mediaBaseUrl}
      />

      <ImageInventoryStrip inventory={result.image_inventory} images={images} mediaBaseUrl={mediaBaseUrl} />

      {/* Visible damages */}
      {result.visible_damages?.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-text-primary mb-2">Visible Damages</h3>
          <div className="space-y-2">
            {result.visible_damages.map((d, i) => (
              <div key={i} className="border border-border-light rounded-[8px] p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-text-primary">{formatEnumLabel(d.element)}</span>
                  <Chip label={d.severity} colors={SEVERITY_COLORS[d.severity]} />
                </div>
                {d.description && <p className="text-xs text-text-secondary mt-1">{d.description}</p>}
                <EvidenceThumbs indices={d.evidence_images} images={images} mediaBaseUrl={mediaBaseUrl} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Risk factors */}
      {result.risk_factors?.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-text-primary mb-2">Risk Factors</h3>
          <div className="flex flex-wrap gap-2">
            {result.risk_factors.map((r, i) => (
              <span key={i} className="text-xs bg-gray-100 text-text-primary rounded-[6px] px-2 py-1">
                {r.factor} <span className="text-text-secondary">· {formatEnumLabel(r.category)}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Protection */}
      {result.protection?.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-text-primary mb-2">Protection Measures</h3>
          <div className="space-y-1">
            {result.protection.map((p, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="text-text-primary">{formatEnumLabel(p.measure)}</span>
                <Chip label={p.status} colors={p.status === "present" ? CONDITION_COLORS.good : undefined} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Liability hazards */}
      {result.liability_hazards?.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-text-primary mb-2">Liability Hazards</h3>
          <ul className="list-disc list-inside text-sm text-text-primary space-y-1">
            {result.liability_hazards.map((h, i) => (
              <li key={i}>
                {formatEnumLabel(h.hazard)}
                {h.description ? ` — ${h.description}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Recommendations */}
      {result.recommendations?.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-text-primary mb-2">Recommendations</h3>
          <ul className="list-disc list-inside text-sm text-text-primary space-y-1">
            {result.recommendations.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        </div>
      )}

      {result.photo_coverage?.areas_not_covered?.length > 0 && (
        <p className="text-xs text-text-secondary">
          Not covered by photos: {result.photo_coverage.areas_not_covered.join(", ")}
        </p>
      )}

      {result.limitations?.length > 0 && (
        <p className="text-xs text-text-secondary italic">{result.limitations.join(" ")}</p>
      )}
    </div>
  );
}

export default function PropertyCaseDetails() {
  const { caseId: _encodedCaseId } = useParams();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const caseId = decodeRefNum(_encodedCaseId);

  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const aiSummary = usePropertyAiSummary();
  const report = usePropertyDownloadReport();
  const [aiExpanded, setAiExpanded] = useState(false);
  // Guards the background kick-off so it fires once per case, not on every
  // render the status happens to still be "idle".
  const aiStartedForRef = useRef(null);

  const loadCase = useCallback(async (refNum) => {
    setLoading(true);
    setError(null);
    try {
      const [assessmentResult, vehicleResult] = await Promise.allSettled([
        getPropertyAssessmentDetail(refNum),
        getPropertyVehicle(refNum),
      ]);
      const assessmentData =
        assessmentResult.status === "fulfilled" ? assessmentResult.value : null;
      const vehicleData =
        vehicleResult.status === "fulfilled" ? vehicleResult.value : null;

      if (!assessmentData && !vehicleData) {
        throw new Error("No data returned for this case");
      }
      setOverview(toPropertyOverview(assessmentData, vehicleData));
    } catch (err) {
      console.error("Error loading property case:", err);
      setError("Failed to load this case");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (caseId) loadCase(caseId);
    aiSummary.reset();
    aiStartedForRef.current = null;
    setAiExpanded(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, loadCase]);

  // Unlike the old GET /get_summary wrapper, Property Insights has no notion
  // of this case at all — the photos themselves are fetched and submitted,
  // not looked up server-side from a ref_num. One place to build that
  // request so the kickoff effect, Try Again, and Regenerate can't drift.
  const runAiSummary = useCallback(() => {
    aiSummary.generate({
      photos: overview?.images,
      mediaBaseUrl: getPropertyMediaBaseUrl(),
      country: overview?.country || countryFromSearch(routerLocation.search),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overview, routerLocation.search]);

  // Kick the summary off in the background once the case's photos are known,
  // so it is already running (often finished) before anyone scrolls down to
  // it. Expanding the section only reveals the result, it does not start it.
  useEffect(() => {
    if (!overview) return;
    if (aiStartedForRef.current === caseId) return;
    if (!overview.images || overview.images.length === 0) return;
    aiStartedForRef.current = caseId;
    runAiSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overview, caseId]);

  if (loading) {
    return (
      <div className="bg-background-primary h-full w-full flex items-center justify-center">
        <p className="text-gray-600">Loading case...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-background-primary h-full w-full flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => navigate("/property-cases")}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Back to Property Cases
          </button>
        </div>
      </div>
    );
  }

  // link_generated_details.country (toPropertyOverview's source) can come
  // back empty — the list already knew the country, so fall back to the
  // ?country= it passed along. Same mechanism as the redesign's equivalent.
  const displayCountry = overview?.country || countryFromSearch(routerLocation.search);
  const statusInfo = getStatusInfo(parseInt(overview?.status, 10));
  const imageBuckets = groupPropertyImagesBySide(overview?.images);
  const mediaBaseUrl = getPropertyMediaBaseUrl();
  // Exterior first — that is how a building is recognised — then interior.
  const headerPhoto =
    [
      ...imageBuckets.exterior,
      ...imageBuckets.interior,
      ...imageBuckets.additional,
    ]
      .map((img) => buildPropertyImageUrl(mediaBaseUrl, img?.picture))
      .find(Boolean) || null;
  // A photo with no location permission still carries lat/long — as the
  // literal string "None", which `img?.lat && img?.long` reads as truthy —
  // so the Map rendered anyway, with every "None" plotted at (0, 0) and
  // nothing else to show for it. Same validity check as the adapter's own
  // noLocationPermission (toPropertyOverview), so the Map and the warning
  // agree on whether there's anything real to plot.
  const allImagesForMap = (overview?.images || [])
    .filter(
      (img) =>
        Number.isFinite(parseFloat(img?.lat)) &&
        Number.isFinite(parseFloat(img?.long)),
    )
    .map((img) => ({ ...img, longitude: img.long }));

  // Progress is surfaced in the collapsed header so the section reads as
  // "working on it" without having to be opened.
  const aiStatusLabel =
    aiSummary.status === "processing"
      ? aiSummary.progress
        ? `Analyzing… (${aiSummary.progress.batches_done ?? 0}/${aiSummary.progress.batches_total ?? "?"})`
        : "Analyzing…"
      : aiSummary.status === "failed"
        ? "Failed"
        : aiSummary.status === "completed"
          ? "Ready"
          : "";

  return (
    <div className="bg-background-primary h-full w-full overflow-y-auto">
      <div className="max-w-5xl mx-auto px-4 lg:px-8 py-6">
        <button
          onClick={() => navigate("/property-cases")}
          className="flex items-center gap-2 text-text-secondary hover:text-text-primary text-sm mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Property Cases
        </button>

        {/* Header */}
        <div className="bg-white rounded-[12px] border border-border-light p-6 shadow-sm mb-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0">
              {/* Same slot as the redesigned page: it was a hard-coded glyph,
                  so a case with nine photographs looked like a case with none. */}
              <div className="w-16 h-16 rounded-[8px] bg-gray-100 flex items-center justify-center shrink-0 overflow-hidden">
                {headerPhoto ? (
                  <img src={headerPhoto} alt="" className="h-full w-full object-cover" />
                ) : (
                  <Building2 className="w-8 h-8 text-gray-400" />
                )}
              </div>
              <div className="min-w-0">
                <h1 className="text-xl font-semibold text-text-primary truncate">
                  {overview?.address || "Address not available"}
                </h1>
                <p className="text-text-secondary text-sm mt-1">
                  Ref: {overview?.refNum || "—"} · Created {formatDateTime(overview?.createdAt)}
                </p>
              </div>
            </div>
            <div className="flex flex-col items-end gap-2 shrink-0">
              {displayCountry && (
                <div className="flex items-center gap-1.5 text-sm text-text-secondary">
                  <span>Country: {displayCountry}</span>
                  {getCountryFlagSrc(displayCountry) && (
                    <img
                      alt={displayCountry}
                      className="h-[14px] w-5 rounded-[2px] object-cover"
                      src={getCountryFlagSrc(displayCountry)}
                    />
                  )}
                </div>
              )}
              {overview?.timeTakenToUpload && (
                <div className="text-sm text-text-secondary">
                  Time taken to upload: {formatUploadDuration(overview.timeTakenToUpload)}
                </div>
              )}
              <div className="flex gap-[8px] items-center">
                <img alt={statusInfo.text} className="w-[24px] h-[24px]" src={statusInfo.icon} />
                <span className="font-medium text-sm" style={{ color: statusInfo.color }}>
                  {statusInfo.text}
                </span>
              </div>
              <button
                type="button"
                onClick={() => report.download(overview?.refNum || caseId)}
                disabled={report.status === "downloading"}
                className="flex items-center gap-1.5 rounded-[8px] border border-border-light px-3 py-1.5 text-xs font-medium text-text-secondary transition-colors hover:text-text-primary hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Download className="w-3.5 h-3.5" />
                {report.status === "downloading" ? "Downloading…" : "Download Report"}
              </button>
              {report.status === "error" && (
                <span className="text-xs text-red-600 max-w-[220px] text-right">{report.error}</span>
              )}
            </div>
          </div>
        </div>

        {/* Warnings */}
        {overview?.warnings?.length > 0 && (
          <div className="bg-[#fffaf0] border border-[#f5cf87] rounded-[12px] p-4 mb-6 flex gap-3">
            <AlertTriangle className="w-5 h-5 text-[#b7791f] shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-[#8a5c07] text-sm mb-1">Warnings</div>
              {overview.warnings.map((w, i) => (
                <p key={i} className="text-[#8a5c07] text-sm">{w}</p>
              ))}
            </div>
          </div>
        )}

        {/* Image counts */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <StatTile label="Uploaded Images" value={overview?.images?.length ?? 0} />
          <StatTile label="Exterior Images" value={imageBuckets.exterior.length} />
          <StatTile label="Interior Images" value={imageBuckets.interior.length} />
          <StatTile label="Additional Images" value={imageBuckets.additional.length} />
        </div>

        {/* Inspection details */}
        <div className="bg-white rounded-[12px] border border-border-light p-6 shadow-sm mb-6">
          <h2 className="text-base font-semibold text-text-primary mb-4">Inspection Details</h2>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
            <DetailRow label="Ergo ID" value={overview?.ergoId} />
            <DetailRow label="Inspection Type" value={formatEnumLabel(overview?.inspectionType)} />
            <DetailRow label="Property Type" value={overview?.propertyType ? formatEnumLabel(overview.propertyType) : ""} />
            <DetailRow label="Time Taken to Upload" value={overview?.timeTakenToUpload} />
            <DetailRow label="Device" value={overview?.deviceMakeModel} />
            <DetailRow label="OS / Browser" value={overview?.osSummary} />
            <DetailRow label="Inspection Mode" value={overview?.inspectionMode ? formatEnumLabel(overview.inspectionMode) : ""} />
            <DetailRow
              label="Internet Speed Range"
              value={
                overview?.minInternetSpeed && overview?.maxInternetSpeed
                  ? `${overview.minInternetSpeed} - ${overview.maxInternetSpeed} Mbps`
                  : ""
              }
            />
          </dl>
        </div>

        {/* Rating / reviewer comment */}
        <div className="bg-white rounded-[12px] border border-border-light p-6 shadow-sm mb-6">
          <h2 className="text-base font-semibold text-text-primary mb-4">TriNetra Ratings</h2>
          {overview?.userRating != null ? (
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-text-primary">Rating:</span>
              <StarRating rating={overview.userRating} />
            </div>
          ) : (
            <p className="text-sm text-text-primary">User did not provide rating.</p>
          )}
          {overview?.reviewerComment && (
            <p className="text-sm text-text-secondary mt-2">
              <span className="font-medium text-text-primary">Comment: </span>
              {overview.reviewerComment}
            </p>
          )}
        </div>

        {/* Images, grouped by side */}
        {IMAGE_SECTIONS.map(({ key, label }) => {
          const images = imageBuckets[key];
          if (!images || images.length === 0) return null;
          return (
            <div key={key} className="bg-white rounded-[12px] border border-border-light p-6 shadow-sm mb-6">
              <h2 className="text-base font-semibold text-text-primary mb-4">{label}</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {images.map((img, i) => {
                  const src = buildPropertyImageUrl(mediaBaseUrl, img?.picture);
                  if (!src) return null;
                  return (
                    <a
                      key={i}
                      href={src}
                      target="_blank"
                      rel="noreferrer"
                      className="aspect-square rounded-[8px] overflow-hidden bg-gray-100 block"
                      title={img?.name ? formatEnumLabel(img.name) : undefined}
                    >
                      <img alt={img?.name || `${label} ${i + 1}`} className="w-full h-full object-cover" src={src} />
                    </a>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Map */}
        {allImagesForMap.length > 0 && (
          <div className="bg-white rounded-[12px] border border-border-light p-6 shadow-sm mb-6">
            <h2 className="text-base font-semibold text-text-primary mb-4">Map</h2>
            <div className="h-[420px] rounded-[8px] overflow-hidden">
              <CustomMap
                uploaded={allImagesForMap}
                setMaxDistance={() => {}}
                showPolygon={false}
                setShowPolygon={() => {}}
              />
            </div>
          </div>
        )}

        {/* AI Summary — last on the page, and generated in the background as
            soon as the case loads so the result is usually ready by the time
            anyone scrolls here. The header button only expands/collapses it. */}
        <div className="bg-white rounded-[12px] border border-border-light p-6 shadow-sm mb-6">
          <button
            onClick={() => setAiExpanded((v) => !v)}
            className="w-full flex items-center justify-between gap-3 text-left"
          >
            <h2 className="text-base font-semibold text-text-primary flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-purple-600" />
              AI Property Summary
            </h2>
            <div className="flex items-center gap-3">
              <span className="text-xs text-text-secondary">{aiStatusLabel}</span>
              <ChevronDown
                className={`w-4 h-4 text-text-secondary transition-transform ${aiExpanded ? "rotate-180" : ""}`}
              />
            </div>
          </button>

          {aiExpanded && (
            <div className="mt-4">
              {aiSummary.status === "failed" && (
                <div>
                  <p className="text-sm text-red-600 mb-3">{aiSummary.error}</p>
                  <button
                    onClick={runAiSummary}
                    className="bg-primary text-white px-4 py-2 rounded-[8px] text-sm font-semibold hover:bg-[#2680c7]"
                  >
                    Try Again
                  </button>
                </div>
              )}

              {aiSummary.status === "completed" && aiSummary.result && (
                <AiSummaryResult
                  result={aiSummary.result}
                  images={aiSummary.images}
                  completedAt={aiSummary.completedAt}
                  mediaBaseUrl={mediaBaseUrl}
                  onRegenerate={runAiSummary}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
